import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updateImage, deleteImage, shareImage, unshareImage } from '../../api/images';
import { getFolders } from '../../api/folders';
import { getCategories } from '../../api/categories';

export default function ImageModal({ image, onClose }) {
  const qc = useQueryClient();
  const [lightbox, setLightbox] = useState(false);

  // Close on Escape — close the zoom viewer first if it's open, else the modal
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (lightbox) setLightbox(false);
      else onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, lightbox]);

  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 shrink-0">
          <p className="font-medium text-white truncate max-w-lg text-sm">{image.original_name}</p>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-xl leading-none ml-4 shrink-0"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col md:flex-row overflow-auto flex-1">
          {/* Preview pane */}
          <div className="flex-1 flex items-center justify-center bg-gray-950 p-4 min-h-48">
            {image.mime_type === 'application/pdf' ? (
              <PdfPreview
                src={`/uploads/originals/${image.filename}`}
                title={image.original_name}
              />
            ) : image.mime_type === 'application/epub+zip' ? (
              <div className="flex flex-col items-center justify-center gap-4 text-center">
                <svg className="w-20 h-20 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-6 14H8v-2h4v2zm4-4H8v-2h8v2zm0-4H8V6h8v2z"/>
                </svg>
                <div>
                  <p className="text-white font-medium">{image.original_name}</p>
                  <p className="text-gray-500 text-sm mt-1">EPUB files cannot be previewed in the browser.</p>
                  <p className="text-gray-500 text-sm">Download to read in an e-reader app.</p>
                </div>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={`/uploads/originals/${image.filename}`}
                  alt={image.original_name}
                  onClick={() => setLightbox(true)}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg cursor-zoom-in"
                />
                <button
                  onClick={() => setLightbox(true)}
                  title="Open full size to read"
                  className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 hover:bg-black/80 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                  </svg>
                  Expand
                </button>
              </div>
            )}
          </div>

          {/* Info pane */}
          <div className="w-full md:w-72 shrink-0 border-t md:border-t-0 md:border-l border-gray-800 overflow-y-auto">
            <ImageInfo image={image} qc={qc} onDeleted={onClose} />
          </div>
        </div>
      </div>

      {lightbox && (
        <ImageLightbox
          src={`/uploads/originals/${image.filename}`}
          alt={image.original_name}
          onClose={() => setLightbox(false)}
        />
      )}
    </div>
  );
}

// Full-screen image viewer with zoom, for reading fine detail (infographics,
// screenshots) without downloading. Default fits the viewport width; zoom in
// to enlarge and scroll to pan. Click the image to toggle 1x / 2x.
function ImageLightbox({ src, alt, onClose }) {
  const [zoom, setZoom] = useState(1);
  const clamp   = (z) => Math.min(5, Math.max(1, +z.toFixed(2)));
  const zoomIn  = () => setZoom((z) => clamp(z + 0.25));
  const zoomOut = () => setZoom((z) => clamp(z - 0.25));
  const reset   = () => setZoom(1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomIn(); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomOut(); }
      else if (e.key === '0') { e.preventDefault(); reset(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const btn = 'h-8 min-w-8 px-1.5 flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-gray-100 leading-none transition-colors';

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col select-none">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 px-4 h-12 shrink-0 border-b border-white/10">
        <p className="text-gray-300 text-sm truncate">{alt}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={zoomOut} className={btn} title="Zoom out (−)">−</button>
          <span className="text-gray-300 text-xs w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button onClick={zoomIn} className={btn} title="Zoom in (+)">+</button>
          <button onClick={reset} className={`${btn} text-xs`} title="Reset (0)">Reset</button>
          <a href={src} target="_blank" rel="noopener noreferrer" className={`${btn} text-xs`} title="Open in new tab">↗</a>
          <button onClick={onClose} className={`${btn} text-xs`} title="Close (Esc)">✕</button>
        </div>
      </div>
      {/* Scrollable image area */}
      <div className="flex-1 overflow-auto">
        <img
          src={src}
          alt={alt}
          onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
          style={{
            width: `${zoom * 100}%`,
            maxWidth: 'none',
            margin: zoom === 1 ? '0 auto' : 0,
            cursor: zoom === 1 ? 'zoom-in' : 'zoom-out',
          }}
          className="block h-auto"
        />
      </div>
    </div>
  );
}

function ImageInfo({ image, qc, onDeleted }) {
  const [folderId,    setFolderId]    = useState(image.folder_id   || '');
  const [categoryId,  setCategoryId]  = useState(image.category_id || '');
  const [shareUrl,    setShareUrl]    = useState(
    image.share_token ? `${window.location.origin}/share/${image.share_token}` : null
  );
  const [copied, setCopied] = useState(false);
  const [err,    setErr]    = useState('');

  const { data: folders    = [] } = useQuery({ queryKey: ['folders'],    queryFn: () => getFolders().then(r => r.data) });
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => getCategories().then(r => r.data) });

  const moveMut = useMutation({
    mutationFn: (patch) => updateImage(image.id, patch),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['images'] }),
    onError:    (e) => setErr(e.response?.data?.error || 'Update failed.'),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteImage(image.id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['images'] }); onDeleted(); },
  });

  const shareMut = useMutation({
    mutationFn: () => image.share_token ? unshareImage(image.id) : shareImage(image.id),
    onSuccess: (res) => {
      if (image.share_token) {
        setShareUrl(null);
        image.share_token = null;
      } else {
        const token = res.data.share_token;
        const url   = `${window.location.origin}/share/${token}`;
        setShareUrl(url);
        image.share_token = token;
      }
      qc.invalidateQueries({ queryKey: ['images'] });
    },
  });

  function copyLink() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Fetch the file as a blob and save it. This forces a real download with the
  // correct filename regardless of the service worker, Content-Disposition, or
  // the browser's handling of inline PDF types.
  async function downloadFile(e) {
    e.preventDefault();
    const url = `/uploads/originals/${image.filename}`;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = image.original_name || image.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch {
      // Fallback: open the file directly so the user can save it manually.
      window.open(url, '_blank', 'noopener');
    }
  }

  const sizeKb   = (image.size_bytes / 1024).toFixed(1);
  const created  = new Date(image.created_at).toLocaleString();

  return (
    <div className="p-4 space-y-4 text-sm">
      {/* Metadata */}
      <div className="space-y-1.5">
        <Row label="Type"    value={image.mime_type} />
        <Row label="Size"    value={`${sizeKb} KB`} />
        <Row label="Source"  value={image.source} />
        <Row label="Added"   value={created} />
      </div>

      <hr className="border-gray-800" />

      {/* Move to folder */}
      <div>
        <label className="label">Folder</label>
        <select
          className="input text-sm"
          value={folderId}
          onChange={(e) => { setFolderId(e.target.value); moveMut.mutate({ folder_id: e.target.value || null }); }}
        >
          <option value="">— No folder —</option>
          {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      {/* Move to category */}
      <div>
        <label className="label">Category</label>
        <select
          className="input text-sm"
          value={categoryId}
          onChange={(e) => { setCategoryId(e.target.value); moveMut.mutate({ category_id: e.target.value || null }); }}
        >
          <option value="">— No category —</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {err && <p className="text-red-400 text-xs">{err}</p>}

      <hr className="border-gray-800" />

      {/* Share */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 uppercase tracking-wider">Share</p>
        {shareUrl ? (
          <div className="space-y-1.5">
            <div className="flex gap-1.5">
              <input
                readOnly
                className="input text-xs py-1 flex-1 font-mono"
                value={shareUrl}
              />
              <button
                className="btn-secondary text-xs px-2 py-1 shrink-0"
                onClick={copyLink}
              >
                {copied ? '✓' : 'Copy'}
              </button>
            </div>
            <button
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
              onClick={() => shareMut.mutate()}
            >
              Revoke link
            </button>
          </div>
        ) : (
          <button className="btn-secondary text-xs w-full" onClick={() => shareMut.mutate()}>
            Generate share link
          </button>
        )}
      </div>

      <hr className="border-gray-800" />

      {/* Actions */}
      <div className="flex gap-2">
        <a
          href={`/uploads/originals/${image.filename}`}
          download={image.original_name}
          onClick={downloadFile}
          className="btn-secondary text-xs flex-1 text-center"
        >
          ↓ Download
        </a>
        {image.mime_type === 'application/pdf' && (
          <a
            href={`/uploads/originals/${image.filename}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-xs flex-1 text-center"
          >
            ↗ Open
          </a>
        )}
        <button
          className="bg-red-900/40 hover:bg-red-900/70 text-red-400 text-xs font-medium px-3 py-2 rounded-lg transition-colors"
          onClick={() => {
            if (confirm(`Delete "${image.original_name}"?`)) deleteMut.mutate();
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// Render a PDF from an in-memory blob URL instead of the network URL.
// A blob: URL is same-origin to this document, so it renders regardless of
// X-Frame-Options / frame-ancestors — needed when SnapVault is embedded in a
// cross-origin iframe (e.g. the CloudDesktop Workspace).
function PdfPreview({ src, title }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed]   = useState(false);

  useEffect(() => {
    let objUrl;
    let cancelled = false;
    setBlobUrl(null);
    setFailed(false);
    fetch(src, { cache: 'no-store' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src]);

  if (failed) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 text-center text-gray-400 text-sm py-12">
        <p>Couldn’t load the preview.</p>
        <p>Use Open or Download to view the PDF.</p>
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <iframe
      src={blobUrl}
      title={title}
      className="w-full rounded-lg bg-white"
      style={{ height: '60vh' }}
    />
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-300 text-right truncate">{value}</span>
    </div>
  );
}
